import React, { useState, useEffect, useCallback } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";
import { colors } from "../AgenticPaymentAirdrop/styles";
import { HelpTitle } from "../AdminHeaderHelp";

// Partner API access requests, read from lp-api. Live keys move real money, so
// an account needs approval before it can create or use one. Accounts that had
// live keys before approval existed start as pending with no business details.
// The website is shown as text, not a link: the applicant typed it.

// Styles are inline: the admin page loads this pack's JS but not its CSS.
const card = {
  background: colors.white, border: `1px solid ${colors.border}`, borderRadius: "8px",
  padding: "20px 24px", marginBottom: "20px",
};
const note = { fontSize: "13px", color: colors.gray600, margin: "0 0 12px" };
const help = { fontSize: "12px", color: colors.gray600, marginTop: "4px" };
const label = { display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" };
const input = {
  width: "100%", maxWidth: "460px", padding: "7px 10px", fontSize: "14px",
  border: `1px solid ${colors.gray400}`, borderRadius: "6px", boxSizing: "border-box",
};
// Header cells stay in view while the table scrolls inside its box.
const th = {
  position: "sticky", top: 0, zIndex: 1, background: colors.white,
  textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.4px",
  color: colors.gray600, padding: "8px 10px", borderBottom: `1px solid ${colors.border}`, whiteSpace: "nowrap",
};
const td = { padding: "8px 10px", fontSize: "13px", borderBottom: `1px solid ${colors.border}`, verticalAlign: "top" };
const tableBox = { overflow: "auto", maxHeight: "70vh" };
const btn = (disabled) => ({
  background: colors.primary, color: "#fff", border: `1px solid ${colors.primary}`, borderRadius: "6px",
  padding: "8px 16px", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = (disabled) => ({ ...btn(disabled), background: "#fff", color: colors.primary });
const dangerBtn = (disabled) => ({ ...btn(disabled), background: colors.danger, borderColor: colors.danger });
const TONES = {
  good: { bg: "#d4edda", fg: "#155724" },
  warn: { bg: "#fff3cd", fg: "#856404" },
  bad: { bg: "#f8d7da", fg: "#721c24" },
};
const Tag = ({ tone, children }) => (
  <span style={{ background: TONES[tone].bg, color: TONES[tone].fg, borderRadius: "10px", padding: "2px 8px", fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap" }}>
    {children}
  </span>
);
const Banner = ({ tone, children }) => (
  <div role="status" style={{ background: TONES[tone].bg, color: TONES[tone].fg, borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", fontSize: "14px" }}>
    {children}
  </div>
);
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
const sub = { color: "#6c757d", fontSize: "12px" };

const regionNames = (() => {
  try { return new Intl.DisplayNames(["en"], { type: "region" }); } catch (e) { return null; }
})();
// "Indonesia (ID)", or the code alone when the browser can't name it.
export const countryLabel = (code) => {
  if (!code) return "-";
  let name = null;
  try { name = regionNames?.of(code); } catch (e) { /* unknown code */ }
  return name && name !== code ? `${name} (${code})` : code;
};

const BUSINESS_TYPES = { individual: "Individual", company: "Company", nonprofit: "Non-profit" };
const CUSTOMER_TYPES = { consumers: "Consumers", businesses: "Businesses", both: "Both" };
const VOLUMES = { under_10k: "Under $10k", "10k_100k": "$10k to $100k", "100k_1m": "$100k to $1M", over_1m: "Over $1M" };

const FILTERS = [
  { value: "pending", text: "Pending" },
  { value: "approved", text: "Approved" },
  { value: "rejected", text: "Rejected" },
  { value: "", text: "All" },
];

export const STATUS = {
  pending: { tone: "warn", text: "Pending" },
  approved: { tone: "good", text: "Approved" },
  rejected: { tone: "bad", text: "Rejected" },
};

// lp-api answers { data: [...], page, per_page, total }.
export const rowsOf = (data) => (Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []);

const PartnerAccess = ({ listUrl, approveUrl, rejectUrl }) => {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(null); // { row, reason }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest(status ? `${listUrl}?status=${status}` : listUrl);
      if (res?.success) {
        setRows(rowsOf(res.data));
        setLoadError(null);
      } else {
        setLoadError(res?.error || "Couldn't load the requests.");
      }
    } catch (e) {
      setLoadError("Couldn't load the requests.");
    } finally {
      setLoading(false);
    }
  }, [listUrl, status]);

  useEffect(() => { load(); }, [load]);

  const post = async (url, body) => {
    try {
      return await postRequest(url, body);
    } catch (e) {
      return { success: false, error: e?.data?.error };
    }
  };

  const who = (row) => row.business_name || row.owner_email || row.owner_user_ext_id;

  const approve = async (row) => {
    setBusy(true);
    setMessage(null);
    const res = await post(approveUrl, { id: row.id });
    if (res?.success) {
      setMessage({ tone: "good", text: `${who(row)} can now use live keys.` });
      await load();
    } else {
      setMessage({ tone: "bad", text: res?.error || "That didn't save." });
    }
    setBusy(false);
  };

  const reject = async () => {
    const reason = rejecting.reason.trim();
    if (!reason) return setMessage({ tone: "bad", text: "Enter a reason. The applicant sees it." });
    const { row } = rejecting;
    setBusy(true);
    setMessage(null);
    const res = await post(rejectUrl, { id: row.id, reason });
    if (res?.success) {
      setMessage({ tone: "good", text: `${who(row)} was rejected. Their live keys stay off.` });
      setRejecting(null);
      await load();
    } else {
      setMessage({ tone: "bad", text: res?.error || "That didn't save." });
    }
    setBusy(false);
  };

  return (
    <div>
      <p style={{ ...note, maxWidth: "760px" }}>
        An account needs approval here before it can create or use live Partner API keys. Sandbox keys
        don't need approval. Accounts that already had live keys show as pending without business details,
        and their keys stay off until you approve them.
      </p>

      {message && <Banner tone={message.tone}>{message.text}</Banner>}

      <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
        {FILTERS.map((f) => (
          <button key={f.value || "all"} type="button" onClick={() => setStatus(f.value)}
            style={status === f.value ? btn(false) : ghostBtn(false)}>
            {f.text}
          </button>
        ))}
      </div>

      {rejecting && (
        <div style={card}>
          <p style={{ ...label, marginBottom: "8px" }}>Reject {who(rejecting.row)}</p>
          <label style={label} htmlFor="partner-reject-reason">Reason</label>
          <input id="partner-reject-reason" type="text" maxLength={500} style={input} value={rejecting.reason}
            onChange={(e) => setRejecting((r) => ({ ...r, reason: e.target.value }))} />
          <div style={help}>The applicant sees this on their Partner API page.</div>
          <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
            <button type="button" style={dangerBtn(busy)} disabled={busy} onClick={reject}>Reject</button>
            <button type="button" style={ghostBtn(busy)} disabled={busy} onClick={() => setRejecting(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={card}>
        {loading ? (
          <p style={note}>Loading…</p>
        ) : loadError ? (
          <Banner tone="bad">{loadError}</Banner>
        ) : rows.length === 0 ? (
          <p style={note}>Nothing here.</p>
        ) : (
          <div style={tableBox}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Account</th>
                  <th style={th}>Business</th>
                  <th style={th}>Contact</th>
                  <th style={th}>Use case</th>
                  <th style={th}><HelpTitle title="Live keys" help="Live API keys this account has active. Live keys move real money." /></th>
                  <th style={th}>Status</th>
                  <th style={th}>Applied</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const s = STATUS[row.status] || { tone: "warn", text: row.status };
                  return (
                    <tr key={row.id}>
                      <td style={td}>
                        <div>{row.owner_name || "-"}</div>
                        <div style={{ color: "#6c757d" }}>{row.owner_email || row.owner_user_ext_id}</div>
                      </td>
                      <td style={td}>
                        <div>{row.business_name || <em>Not given (had live keys before approval)</em>}</div>
                        {row.legal_name && <div style={sub}>{row.legal_name}</div>}
                        {(row.business_type || row.country) && (
                          <div style={sub}>
                            {[BUSINESS_TYPES[row.business_type] || row.business_type, row.country && countryLabel(row.country)]
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        )}
                        {row.website && <div style={{ ...sub, wordBreak: "break-all" }}>{row.website}</div>}
                      </td>
                      <td style={td}>
                        {row.contact_name ? (
                          <>
                            <div>{row.contact_name}</div>
                            {row.contact_role && <div style={sub}>{row.contact_role}</div>}
                            {row.contact_email && <div style={{ ...sub, wordBreak: "break-all" }}>{row.contact_email}</div>}
                          </>
                        ) : "-"}
                      </td>
                      <td style={{ ...td, maxWidth: "320px" }}>
                        <div style={{ whiteSpace: "pre-wrap" }}>{row.use_case || "-"}</div>
                        {(row.customer_type || row.monthly_volume) && (
                          <div style={{ ...sub, marginTop: "4px" }}>
                            {[CUSTOMER_TYPES[row.customer_type] || row.customer_type, VOLUMES[row.monthly_volume] || row.monthly_volume]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        )}
                        {row.countries?.length > 0 && (
                          <div style={sub}>Serves: {row.countries.map(countryLabel).join(", ")}</div>
                        )}
                      </td>
                      <td style={td}>{row.active_live_keys ?? "-"}</td>
                      <td style={td}>
                        <Tag tone={s.tone}>{s.text}</Tag>
                        {row.reviewed_by && (
                          <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "4px" }}>
                            {row.reviewed_by}, {when(row.reviewed_at)}
                          </div>
                        )}
                        {row.reject_reason && (
                          <div style={{ fontSize: "12px", marginTop: "4px" }}>{row.reject_reason}</div>
                        )}
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{when(row.submitted_at || row.created_at)}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        {row.status !== "approved" && (
                          <button type="button" style={btn(busy)} disabled={busy} onClick={() => approve(row)}>Approve</button>
                        )}
                        {row.status !== "rejected" && (
                          <button type="button" style={{ ...ghostBtn(busy), marginLeft: "8px" }} disabled={busy}
                            onClick={() => setRejecting({ row, reason: "" })}>
                            Reject
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerAccess;
